import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlandokumenterForPlan, useFetchPlandokumentAttachmentUrl } from '@origo/planregister';
import { Notice } from '@origo/origo-ui';
import PlanDetailSkeleton from '../../components/PlanDetailSkeleton';
import DocumentsPage from './DocumentsPage';
import { sortNewestFirst, toDocumentItem } from '../../mappers/toDocuments';
import { useDocumentManagement } from './hooks/useDocumentManagement';

interface DocumentsContainerProps {
    napId: string;
}

const DocumentsContainer: React.FC<DocumentsContainerProps> = ({ napId }) => {
    const { t } = useTranslation();
    const { data, isLoading, isError } = usePlandokumenterForPlan(napId);
    const fetchAttachmentUrl = useFetchPlandokumentAttachmentUrl();

    // Nyeste øverst — sorterer på dokumentdato (desc). Dokumenter uten
    // dato havner sist, sortert stabilt etter opprinnelig array-orden.
    const items = useMemo(() => sortNewestFirst(data ?? []).map(doc => toDocumentItem(doc, t)), [data, t]);

    const dm = useDocumentManagement({ items, data, napId });

    if (isLoading) return <PlanDetailSkeleton title={t('documents.title')} subtitle={t('documents.subtitle')} variant="list" />;
    if (isError) return <Notice variant="danger">{t('common.errorLoading')}</Notice>;

    return (
        <DocumentsPage
            items={items}
            onOpen={async id => {
                const doc = data?.find(d => String(d.objectId) === id);
                if (!doc?.referansedokumentfil) return;
                const blobUrl = await fetchAttachmentUrl(doc.referansedokumentfil);
                if (blobUrl) window.open(blobUrl, '_blank', 'noopener,noreferrer');
            }}
            newDocument={dm.newDocument}
            onNewOpen={dm.openNew}
            onNewClose={dm.closeNew}
            onNewChange={dm.changeNew}
            onNewFileChange={dm.changeNewFile}
            onNewSave={dm.saveNew}
            onDeleteRequest={dm.requestDelete}
            pendingDelete={dm.pendingDelete}
            deleteBlokkering={dm.deleteBlokkering}
            deleteAvventerPlan={dm.deleteAvventerPlan}
            onDeleteConfirm={dm.confirmDelete}
            onDeleteCancel={dm.cancelDelete}
            isDeleting={dm.isDeleting}
        />
    );
};

export default DocumentsContainer;
